import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ApiError } from '../../api/client';
import { num } from '../../format';
import { importBom, type ImportPreview, type ImportRow } from './api';

interface Props {
  versionId: string;
  onImported: () => void;
  onClose: () => void;
}

/**
 * Importing a BOM in two steps: look, then write.
 *
 * The preview is not politeness. A part number in a file matches the catalogue
 * exactly, approximately, or not at all, and only the first of those is safe to
 * act on unasked — an import that quietly picks the closest match puts the wrong
 * part in the bill of materials, and nobody finds out until the board is built.
 */
export function BomImport({ versionId, onImported, onClose }: Props) {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [chosen, setChosen] = useState<Record<number, string | null>>({});
  const [replace, setReplace] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fail = (exc: unknown) => setError(exc instanceof ApiError ? exc.code : 'unknown');

  const look = useMutation({
    mutationFn: (chosenFile: File) => importBom(versionId, chosenFile),
    onSuccess: (result) => {
      setPreview(result);
      setChosen({});
      setError(null);
    },
    onError: fail,
  });

  const write = useMutation({
    mutationFn: () =>
      importBom(versionId, file as File, {
        commit: true,
        replace,
        // Rows nobody resolved are left out rather than refused: the file often
        // carries mounting hardware and stickers that are not in the catalogue
        // and never will be.
        skipUnmatched: true,
        resolutions: Object.entries(chosen).map(([rowNo, componentId]) => ({
          row_no: Number(rowNo),
          component_id: componentId,
        })),
      }),
    onSuccess: () => onImported(),
    onError: fail,
  });

  // `??` would be wrong here: choosing "skip" stores null deliberately, and it
  // has to win over the server's proposal rather than fall back to it.
  const decided = (row: ImportRow): string | null =>
    row.row_no in chosen ? (chosen[row.row_no] ?? null) : row.component_id;

  const willWrite = preview?.rows.filter((row) => decided(row) !== null).length ?? 0;

  return (
    <section className="bom-import">
      <div className="panel__section-head">
        <h3>{t('specs.import.title')}</h3>
        <button type="button" onClick={onClose}>
          {t('common.cancel')}
        </button>
      </div>

      <p className="panel__hint">{t('specs.import.hint')}</p>
      {/* Said here rather than left to be discovered: the way to get a file
          this understands is to export a version and edit it. */}
      <p className="hint">{t('specs.import.templateHint')}</p>

      <div className="bom-import__pick">
        <input
          type="file"
          accept=".csv,.txt,.xlsx,.xlsm"
          onChange={(event) => {
            const picked = event.target.files?.[0] ?? null;
            setFile(picked);
            setPreview(null);
            if (picked) look.mutate(picked);
          }}
        />
        {look.isPending && <span className="panel__hint">{t('common.loading')}</span>}
      </div>

      {error && (
        <p className="panel__error">{t(`specs.errors.${error}`, t('specs.errors.unknown'))}</p>
      )}

      {preview && (
        <>
          <p className="bom-import__summary">
            {t('specs.import.summary', {
              total: preview.rows.length,
              matched: preview.matched,
              unmatched: preview.unmatched,
            })}
          </p>

          <div className="bom-import__rows">
            <table>
              <thead>
                <tr>
                  <th>{t('specs.import.row')}</th>
                  <th>{t('specs.designators')}</th>
                  <th>{t('specs.import.fromFile')}</th>
                  <th>{t('specs.quantity')}</th>
                  <th>{t('specs.import.matchedTo')}</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((row) => {
                  const value = decided(row);
                  return (
                    <tr
                      key={row.row_no}
                      className={value === null ? 'bom-import__row--unmatched' : undefined}
                    >
                      <td>{row.row_no}</td>
                      <td>{row.designators ?? ''}</td>
                      <td className="bom-import__query">{row.query}</td>
                      <td>{num(row.quantity)}</td>
                      <td>
                        <select
                          value={value ?? ''}
                          onChange={(event) =>
                            setChosen((current) => ({
                              ...current,
                              [row.row_no]: event.target.value || null,
                            }))
                          }
                        >
                          <option value="">{t('specs.import.skip')}</option>
                          {/* The match the server proposed comes first, then the
                              alternatives it could offer, so accepting is one
                              press and disagreeing is one more. */}
                          {row.component_id && (
                            <option value={row.component_id}>
                              {row.component_code} — {row.component_name}
                            </option>
                          )}
                          {row.candidates
                            .filter((item) => item.component_id !== row.component_id)
                            .map((item) => (
                              <option key={item.component_id} value={item.component_id}>
                                {item.internal_code} — {item.name}
                              </option>
                            ))}
                        </select>
                        <span className={`bom-import__match bom-import__match--${row.match}`}>
                          {t(`specs.import.match.${row.match}`)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="bom-import__actions">
            <label className="bom-import__replace">
              <input
                type="checkbox"
                checked={replace}
                onChange={(event) => setReplace(event.target.checked)}
              />
              {t('specs.import.replace')}
            </label>
            <button
              type="button"
              disabled={write.isPending || willWrite === 0}
              onClick={() => write.mutate()}
            >
              {t('specs.import.write', { n: willWrite })}
            </button>
          </div>
        </>
      )}
    </section>
  );
}
